import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Pencil, Trash2, Plus, Building2, FolderKanban, Receipt, Headphones, FileText, Link as LinkIcon, Copy, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColor = (s) => {
  const map = { active: "bg-green-500/10 text-green-400", inactive: "bg-neutral-500/10 text-[#857973]", planning: "bg-blue-500/10 text-blue-400", in_progress: "bg-[#F59073]/10 text-[#F59073]", completed: "bg-green-500/10 text-green-400", paid: "bg-green-500/10 text-green-400", draft: "bg-neutral-500/10 text-[#857973]", sent: "bg-blue-500/10 text-blue-400", overdue: "bg-red-500/10 text-red-400", open: "bg-yellow-500/10 text-yellow-400", resolved: "bg-green-500/10 text-green-400" };
  return map[s] || "bg-[#1E3050]/40 text-[#857973]";
};

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [tab, setTab] = useState("overview");
  const [portalLink, setPortalLink] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/customers/${id}`, { withCredentials: true })
      .then(r => { setCustomer(r.data); setForm(r.data); })
      .catch(() => navigate("/admin/customers"));
  }, [id, navigate]);

  const save = async () => {
    const { data } = await axios.put(`${API}/admin/customers/${id}`, {
      company_name: form.company_name, org_number: form.org_number, contact_name: form.contact_name,
      email: form.email, phone: form.phone, billing_email: form.billing_email, billing_address: form.billing_address,
      country: form.country, vat_number: form.vat_number, reverse_charge: form.reverse_charge,
      status: form.status, assigned_to: form.assigned_to, services: form.services,
    }, { withCredentials: true });
    setCustomer({ ...customer, ...data });
    setEditing(false);
  };

  const deleteCustomer = async () => {
    if (!window.confirm("Delete this customer and all associated data?")) return;
    await axios.delete(`${API}/admin/customers/${id}`, { withCredentials: true });
    navigate("/admin/customers");
  };

  const generatePortalLink = async () => {
    const { data } = await axios.post(`${API}/admin/customers/${id}/portal-link`, {}, { withCredentials: true });
    const url = `${window.location.origin}/portal/${data.portal_token}`;
    setPortalLink(url);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  if (!customer) return <div className="text-[#857973]">Loading...</div>;

  const tabs = [
    { key: "overview", label: "Overview", icon: Building2 },
    { key: "projects", label: `Projects (${customer.projects?.length || 0})`, icon: FolderKanban },
    { key: "invoices", label: `Invoices (${customer.invoices?.length || 0})`, icon: Receipt },
    { key: "agreements", label: `Agreements (${customer.agreements?.length || 0})`, icon: FileText },
    { key: "tickets", label: `Tickets (${customer.tickets?.length || 0})`, icon: Headphones },
  ];

  return (
    <div data-testid="customer-detail">
      <button onClick={() => navigate("/admin/customers")} className="flex items-center gap-2 text-sm text-[#857973] hover:text-white mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Customers
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">{customer.company_name}</h1>
          <p className="text-sm text-[#857973]">{customer.org_number || "No org number"} &middot; {customer.contact_name}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={generatePortalLink} data-testid="generate-portal-link-btn" className="flex items-center gap-1.5 text-xs bg-[#F59073]/10 text-[#F59073] border border-[#F59073]/30 px-3 py-2 rounded-sm hover:bg-[#F59073]/20 transition-colors" title="Generate & copy customer portal link">
            {copied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><LinkIcon className="w-3.5 h-3.5" /> Portal link</>}
          </button>
          <button onClick={() => setEditing(!editing)} className="text-[#857973] hover:text-white p-2"><Pencil className="w-4 h-4" /></button>
          <button onClick={deleteCustomer} className="text-[#857973] hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      {portalLink && (
        <div className="bg-[#0F1F3A] border border-[#F59073]/30 rounded-sm p-4 mb-6 flex items-center gap-3">
          <LinkIcon className="w-4 h-4 text-[#F59073] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-mono text-[#857973] tracking-wider mb-1">CUSTOMER PORTAL LINK (copied to clipboard)</p>
            <input readOnly value={portalLink} className="w-full bg-transparent text-white text-xs truncate outline-none font-mono" data-testid="portal-link-input" />
          </div>
          <button onClick={() => { navigator.clipboard.writeText(portalLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className="text-[#857973] hover:text-white p-2 flex-shrink-0">
            <Copy className="w-4 h-4" />
          </button>
        </div>
      )}

      {editing && (
        <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-6 mb-6 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <input value={form.company_name || ""} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} placeholder="Company" className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
            <input value={form.contact_name || ""} onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))} placeholder="Contact" className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
            <input value={form.email || ""} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email" className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
            <input value={form.phone || ""} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="Phone" className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
            <input value={form.org_number || ""} onChange={e => setForm(p => ({ ...p, org_number: e.target.value }))} placeholder="Org number" className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
            <select value={form.country || "SE"} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
              <option value="SE">Sverige (SE)</option><option value="NO">Norge (NO)</option><option value="DK">Danmark (DK)</option><option value="FI">Finland (FI)</option>
              <option value="EE">Estland (EE)</option><option value="DE">Tyskland (DE)</option><option value="GB">UK (GB)</option><option value="US">USA (US)</option>
              <option value="NL">Nederländerna (NL)</option><option value="FR">Frankrike (FR)</option><option value="ES">Spanien (ES)</option><option value="IT">Italien (IT)</option>
            </select>
            <select value={form.status || "active"} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
              <option value="active">Active</option><option value="inactive">Inactive</option><option value="churned">Churned</option>
            </select>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <input value={form.vat_number || ""} onChange={e => setForm(p => ({ ...p, vat_number: e.target.value }))} placeholder="VAT number (e.g. SE556012345601)" className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
            <select value={form.reverse_charge || "auto"} onChange={e => setForm(p => ({ ...p, reverse_charge: e.target.value }))} data-testid="reverse-charge-select" className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
              <option value="auto">Moms: Auto (baserat på land)</option>
              <option value="never">Inkl. moms 24%</option>
              <option value="always">Omvänd moms (0%)</option>
              <option value="no_vat">Utan moms</option>
            </select>
          </div>
          <input value={form.billing_email || ""} onChange={e => setForm(p => ({ ...p, billing_email: e.target.value }))} placeholder="Billing email" className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
          <input value={form.billing_address || ""} onChange={e => setForm(p => ({ ...p, billing_address: e.target.value }))} placeholder="Billing address" className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
          <div className="flex gap-2">
            <button onClick={save} className="bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium">Save</button>
            <button onClick={() => { setEditing(false); setForm(customer); }} className="bg-white/10 text-white px-4 py-2 rounded-sm text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="flex gap-1 border-b border-[#1E3050] mb-6">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-3 text-sm transition-colors border-b-2 ${tab === key ? "border-[#F59073] text-[#F59073]" : "border-transparent text-[#857973] hover:text-white"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-6">
            <h3 className="font-mono text-[10px] tracking-[0.15em] text-[#857973] mb-4">CONTACT INFO</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-[#857973]">Email</span><span className="text-white">{customer.email}</span></div>
              <div className="flex justify-between"><span className="text-[#857973]">Phone</span><span className="text-white">{customer.phone || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#857973]">Contact</span><span className="text-white">{customer.contact_name}</span></div>
              <div className="flex justify-between"><span className="text-[#857973]">Status</span><span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${statusColor(customer.status)}`}>{customer.status}</span></div>
            </div>
          </div>
          <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-6">
            <h3 className="font-mono text-[10px] tracking-[0.15em] text-[#857973] mb-4">BILLING</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-[#857973]">Org Nr</span><span className="text-white">{customer.org_number || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#857973]">Land</span><span className="text-white">{customer.country || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#857973]">VAT-nummer</span><span className="text-white">{customer.vat_number || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#857973]">Omvänd moms</span><span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${customer.reverse_charge === "always" ? "bg-green-500/10 text-green-400" : customer.reverse_charge === "never" ? "bg-red-500/10 text-red-400" : "bg-blue-500/10 text-blue-400"}`}>{customer.reverse_charge === "always" ? "Alltid på" : customer.reverse_charge === "never" ? "Aldrig" : "Auto"}</span></div>
              <div className="flex justify-between"><span className="text-[#857973]">Sparat kort</span>
                {customer.saved_card?.last4 ? (
                  <span className="text-white flex items-center gap-2">
                    <span className="text-xs bg-green-500/10 text-green-400 px-2 py-0.5 rounded-sm font-mono">{customer.saved_card.brand?.toUpperCase()}</span>
                    •••• {customer.saved_card.last4}
                    <span className="text-[#857973] text-xs">{customer.saved_card.exp_month}/{customer.saved_card.exp_year}</span>
                  </span>
                ) : <span className="text-[#857973]">Inget kort sparat</span>}
              </div>
              <div className="flex justify-between"><span className="text-[#857973]">Billing email</span><span className="text-white">{customer.billing_email || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#857973]">Address</span><span className="text-white">{customer.billing_address || "—"}</span></div>
            </div>
          </div>
        </div>
      )}

      {tab === "projects" && (
        <div className="space-y-3">
          {(customer.projects || []).map(p => (
            <div key={p.id} className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4 flex items-center justify-between">
              <div>
                <h4 className="text-sm text-white font-manrope">{p.title}</h4>
                <p className="text-xs text-[#857973]">{p.description?.slice(0, 80)}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${statusColor(p.status)}`}>{p.status?.replace("_", " ")}</span>
            </div>
          ))}
          {(!customer.projects || customer.projects.length === 0) && <p className="text-sm text-[#857973] text-center py-8">No projects yet</p>}
        </div>
      )}

      {tab === "invoices" && (
        <div className="space-y-3">
          {(customer.invoices || []).map(inv => (
            <div key={inv.id} className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4 flex items-center justify-between">
              <div>
                <h4 className="text-sm text-white font-manrope">{inv.items?.[0]?.description || "Invoice"}</h4>
                <p className="text-xs text-[#857973]">{inv.type} &middot; {inv.due_date || "No due date"}</p>
              </div>
              <div className="text-right">
                <span className="text-sm text-white font-mono">{inv.total_amount?.toLocaleString()} {inv.currency}</span>
                <span className={`block text-xs px-2 py-0.5 rounded-sm font-mono mt-1 ${statusColor(inv.status)}`}>{inv.status}</span>
              </div>
            </div>
          ))}
          {(!customer.invoices || customer.invoices.length === 0) && <p className="text-sm text-[#857973] text-center py-8">No invoices yet</p>}
        </div>
      )}

      {tab === "agreements" && (
        <div className="space-y-3">
          {(customer.agreements || []).map(a => (
            <div key={a.id} className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4 flex items-center justify-between">
              <div>
                <h4 className="text-sm text-white font-manrope">{a.title}</h4>
                <p className="text-xs text-[#857973]">{a.type} &middot; {a.billing_interval} &middot; {a.start_date || "No start date"}</p>
              </div>
              <div className="text-right">
                <span className="text-sm text-white font-mono">{a.amount?.toLocaleString()} {a.currency}</span>
                <span className={`block text-xs px-2 py-0.5 rounded-sm font-mono mt-1 ${statusColor(a.status)}`}>{a.status}</span>
              </div>
            </div>
          ))}
          {(!customer.agreements || customer.agreements.length === 0) && <p className="text-sm text-[#857973] text-center py-8">No agreements yet</p>}
        </div>
      )}

      {tab === "tickets" && (
        <div className="space-y-3">
          {(customer.tickets || []).map(t => (
            <div key={t.id} className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4 flex items-center justify-between">
              <div>
                <h4 className="text-sm text-white font-manrope">{t.title}</h4>
                <p className="text-xs text-[#857973]">{t.priority} priority</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${statusColor(t.status)}`}>{t.status}</span>
            </div>
          ))}
          {(!customer.tickets || customer.tickets.length === 0) && <p className="text-sm text-[#857973] text-center py-8">No tickets yet</p>}
        </div>
      )}
    </div>
  );
}
